import { isAuthenticated } from "@biggeo/bg-common";
import {
    Applications,
    AuthenticatedUser,
    Organization,
    ProductExtendedProductUserExtended,
    ProductPermission,
    UserInvite,
    useCreateOrganizationMutation,
    useCreateOrganizationUserMutation,
    useFetchAllOrgUserByUserIdLazyQuery,
    useGenerateTokenByEmailMutation,
    useSwapOrgAuthenticationMutation,
    useSwapProductAuthenticationMutation,
    useValidateInvitedUserWithTokenMutation,
    useValidateUserWithTokenMutation,
} from "@biggeo/bg-server-lib/studio";
import {
    LoginForm,
    LoginOrganizationSelectForm,
    LoginSignUpCodeForm,
    SetUpOrganizationInformationForm,
} from "@biggeo/bg-ui/lab";
import { datascapeLogoPng } from "@biggeo/bg-ui/lab/icons";
import {
    ADMIN_PRODUCT_PERMISSION,
    ADMIN_STUDIO_PERMISSION,
} from "@biggeo/bg-utils";
import { Success } from "@vividtheory/remotedata";
import compact from "lodash/compact";
import isEmpty from "lodash/isEmpty";
import { useState } from "react";
import { useDispatch } from "react-redux";
import { useNavigate } from "react-router";
import { useSearchParams } from "react-router-dom";
import { match } from "ts-pattern";

import CreateOrganizationPrompt from "../../auth/components/CreateOrganizationPrompt";
import { CreateEditDatascapeContainer } from "../../auth/containers/CreateEditDatascapeContainer";
import { unwrapMaybe } from "../../common/utils/helpers";
import { Routes } from "../../navigation/redux/model";
import { useErrorToast } from "../../toaster/containers/redux/hooks";
import { useAuthenticatedUser } from "../redux/hooks";
import { userActions } from "../redux/model";

enum SignInStep {
    email = "email",
    token = "token",
    createOrgPrompt = "createOrgPrompt",
    createOrg = "createOrg",
    selectOrg = "selectOrg",
    createProduct = "createProduct",
}

type EmailStep = {
    readonly step: SignInStep.email;
    readonly email?: string;
};

type TokenStep = {
    readonly step: SignInStep.token;
    readonly email: string;
};

type CreateOrgPromptStep = {
    readonly step: SignInStep.createOrgPrompt;
    readonly user: AuthenticatedUser;
};

type CreateOrgStep = {
    readonly step: SignInStep.createOrg;
    readonly user: AuthenticatedUser;
};

type SelectOrgStep = {
    readonly step: SignInStep.selectOrg;
    readonly user: AuthenticatedUser;
    readonly organizations: readonly Organization[];
};

type CreateProductStep = {
    readonly step: SignInStep.createProduct;
    readonly user: AuthenticatedUser;
    readonly organization: Organization;
};

type SignInStepType =
    | EmailStep
    | TokenStep
    | CreateOrgPromptStep
    | CreateOrgStep
    | SelectOrgStep
    | CreateProductStep;

const SignInContainer = ({
    redirect,
    invite,
}: {
    readonly redirect?: string;
    readonly invite?: UserInvite;
}) => {
    const dispatch = useDispatch();
    const toPage = useNavigate();
    const errorToast = useErrorToast();
    const authenticatedUser = useAuthenticatedUser();
    const [searchParams] = useSearchParams();

    const path = redirect ?? Routes.home;
    const userIsAuthenticated = isAuthenticated(authenticatedUser);
    const emailInQueryParams = searchParams.get("email");

    const [currentStep, setCurrentStep] = useState<SignInStepType>(
        emailInQueryParams
            ? { step: SignInStep.token, email: emailInQueryParams }
            : { step: SignInStep.email }
    );

    const toStepFallback = () => {
        errorToast("Something went wrong");
        toEmailStep();
    };

    const toEmailStep = (email?: string) => {
        setCurrentStep({ step: SignInStep.email, email });
    };

    const toTokenStep = (email: string) => {
        setCurrentStep({ step: SignInStep.token, email });
    };

    const toCreateOrgPromptStep = (user: AuthenticatedUser) => {
        setCurrentStep({ step: SignInStep.createOrgPrompt, user });
    };

    const toCreateOrgStep = (user: AuthenticatedUser) => {
        setCurrentStep({ step: SignInStep.createOrg, user });
    };

    const toSelectOrgStep = (
        user: AuthenticatedUser,
        organizations: readonly Organization[]
    ) => {
        setCurrentStep({ step: SignInStep.selectOrg, user, organizations });
    };

    const toCreateProductStep = (
        user: AuthenticatedUser,
        organization: Organization
    ) => {
        setCurrentStep({ step: SignInStep.createProduct, user, organization });
    };

    // ███████╗███╗   ███╗ █████╗ ██╗██╗
    // ██╔════╝████╗ ████║██╔══██╗██║██║
    // █████╗  ██╔████╔██║███████║██║██║
    // ██╔══╝  ██║╚██╔╝██║██╔══██║██║██║
    // ███████╗██║ ╚═╝ ██║██║  ██║██║███████╗
    // ╚══════╝╚═╝     ╚═╝╚═╝  ╚═╝╚═╝╚══════╝

    const {
        executeMutation: generateTokenByEmail,
        mutationReturn: [_, { loading: loginLoading }],
    } = useGenerateTokenByEmailMutation();

    const handleSignIn = (email: string) => {
        generateTokenByEmail({
            variables: { email },
            onCompleted: (result) => {
                const { userExist } = result.generateTokenByEmail;
                if (!userExist) {
                    errorToast("User with this email does not exist");
                } else {
                    toTokenStep(email);
                }
            },
        });
    };

    // ████████╗ ██████╗ ██╗  ██╗███████╗███╗   ██╗
    // ╚══██╔══╝██╔═══██╗██║ ██╔╝██╔════╝████╗  ██║
    //    ██║   ██║   ██║█████╔╝ █████╗  ██╔██╗ ██║
    //    ██║   ██║   ██║██╔═██╗ ██╔══╝  ██║╚██╗██║
    //    ██║   ╚██████╔╝██║  ██╗███████╗██║ ╚████║
    //    ╚═╝    ╚═════╝ ╚═╝  ╚═╝╚══════╝╚═╝  ╚═══╝

    const {
        executeMutation: validateUserWithToken,
        mutationReturn: [_v, { loading: loginCodeLoading }],
    } = useValidateUserWithTokenMutation();

    const { executeQuery: fetchAllOrgUsersByUserId } =
        useFetchAllOrgUserByUserIdLazyQuery();

    const {
        executeMutation: validateInvitedUserWithToken,
        mutationReturn: [_i, { loading: inviteLoginCodeLoading }],
    } = useValidateInvitedUserWithTokenMutation();

    const handleUserValidationSuccess = async (
        authenticatedUser: AuthenticatedUser
    ) => {
        dispatch(userActions.setUserRemoteData(new Success(authenticatedUser)));

        await fetchAllOrgUsersByUserId({
            variables: { userId: authenticatedUser.user.id },
            onCompleted: (orgUsers) => {
                const organizations = compact(
                    orgUsers.fetchAllOrgUserByUserId.organizationUsers
                        .filter((c) => !c.organizationUser.isSupport)
                        .map((ou) => ou.organization)
                );

                if (isEmpty(organizations)) {
                    toCreateOrgPromptStep(authenticatedUser);
                } else {
                    toSelectOrgStep(authenticatedUser, organizations);
                }
            },
        });
    };

    const handleValidateInvitedUser = (token: string, invite: UserInvite) => {
        validateInvitedUserWithToken({
            variables: {
                input: {
                    fkInviteId: invite.id,
                    email: invite.email,
                    token,
                    productPermissions: [ProductPermission.datascapeAi],
                },
            },
            onCompleted: async (result) => {
                await handleUserValidationSuccess(
                    result.validateInvitedUserWithToken
                );
            },
        });
    };

    const handleValidateRegularUser = async (email: string, token: string) => {
        await validateUserWithToken({
            variables: { input: { email, token } },
            onCompleted: async (result) => {
                await handleUserValidationSuccess(result.validateUserWithToken);
            },
            onError: () => {
                errorToast("Failed to validate user");
            },
        });
    };

    const handleValidate = (email: string, token: string) => {
        if (invite) {
            handleValidateInvitedUser(token, invite);
        } else {
            handleValidateRegularUser(email, token);
        }
    };

    const handleResend = (email: string) =>
        generateTokenByEmail({ variables: { email } });

    //  ██████╗ ██████╗  ██████╗  █████╗ ███╗   ██╗██╗███████╗ █████╗ ████████╗██╗ ██████╗ ███╗   ██╗    ██████╗ ██████╗  ██████╗ ███████╗██╗██╗     ███████╗
    // ██╔═══██╗██╔══██╗██╔════╝ ██╔══██╗████╗  ██║██║╚══███╔╝██╔══██╗╚══██╔══╝██║██╔═══██╗████╗  ██║    ██╔══██╗██╔══██╗██╔═══██╗██╔════╝██║██║     ██╔════╝
    // ██║   ██║██████╔╝██║  ███╗███████║██╔██╗ ██║██║  ███╔╝ ███████║   ██║   ██║██║   ██║██╔██╗ ██║    ██████╔╝██████╔╝██║   ██║█████╗  ██║██║     █████╗
    // ██║   ██║██╔══██╗██║   ██║██╔══██║██║╚██╗██║██║ ███╔╝  ██╔══██║   ██║   ██║██║   ██║██║╚██╗██║    ██╔═══╝ ██╔══██╗██║   ██║██╔══╝  ██║██║     ██╔══╝
    // ╚██████╔╝██║  ██║╚██████╔╝██║  ██║██║ ╚████║██║███████╗██║  ██║   ██║   ██║╚██████╔╝██║ ╚████║    ██║     ██║  ██║╚██████╔╝██║     ██║███████╗███████╗
    //  ╚═════╝ ╚═╝  ╚═╝ ╚═════╝ ╚═╝  ╚═╝╚═╝  ╚═══╝╚═╝╚══════╝╚═╝  ╚═╝   ╚═╝   ╚═╝ ╚═════╝ ╚═╝  ╚═══╝    ╚═╝     ╚═╝  ╚═╝ ╚═════╝ ╚═╝     ╚═╝╚══════╝╚══════╝

    const {
        executeMutation: createOrganization,
        mutationReturn: [_co, { loading: createOrganizationLoading }],
    } = useCreateOrganizationMutation();

    const {
        executeMutation: createOrganizationUser,
        mutationReturn: [_cou, { loading: createOrganizationUserLoading }],
    } = useCreateOrganizationUserMutation();

    const handleOrganizationProfile = async (values: Partial<Organization>) => {
        if (userIsAuthenticated) {
            await createOrganization({
                variables: {
                    input: {
                        name: values.name || "",
                        industry: values.industry || [],
                        country: values.country || "",
                        website: values.website,
                        description: values.description,
                        phoneCountryCode: values.phoneCountryCode,
                        phone: values.phone,
                    },
                },
                onCompleted: async (orgData) => {
                    await createOrganizationUser({
                        variables: {
                            input: {
                                fkUserId: authenticatedUser.user.id,
                                fkOrganizationId: orgData.createOrganization.id,
                                productPermissions: ADMIN_PRODUCT_PERMISSION,
                                studioPermissions: ADMIN_STUDIO_PERMISSION,
                                isOwner: true,
                                isSupport: false,
                            },
                        },
                        onCompleted: async () => {
                            await swapOrgAuthentication({
                                variables: {
                                    organizationId:
                                        orgData.createOrganization.id,
                                },
                                onCompleted: (result) => {
                                    dispatch(
                                        userActions.onAuthenticationValidation({
                                            type: "success",
                                            v: new Success(
                                                result.swapOrgAuthentication
                                            ),
                                        })
                                    );

                                    if (userIsAuthenticated) {
                                        toCreateProductStep(
                                            authenticatedUser,
                                            orgData.createOrganization
                                        );
                                    } else {
                                        toStepFallback();
                                    }
                                },
                            });
                        },
                        onError: () => {
                            errorToast("Failed to create organization user");
                        },
                    });
                },
                onError: () => {
                    errorToast("Failed to create organization");
                },
            });
        } else {
            toStepFallback();
        }
    };

    // ███████╗███████╗██╗     ███████╗ ██████╗████████╗     ██████╗ ██████╗  ██████╗  █████╗ ███╗   ██╗██╗███████╗ █████╗ ████████╗██╗ ██████╗ ███╗   ██╗
    // ██╔════╝██╔════╝██║     ██╔════╝██╔════╝╚══██╔══╝    ██╔═══██╗██╔══██╗██╔════╝ ██╔══██╗████╗  ██║██║╚══███╔╝██╔══██╗╚══██╔══╝██║██╔═══██╗████╗  ██║
    // ███████╗█████╗  ██║     █████╗  ██║        ██║       ██║   ██║██████╔╝██║  ███╗███████║██╔██╗ ██║██║  ███╔╝ ███████║   ██║   ██║██║   ██║██╔██╗ ██║
    // ╚════██║██╔══╝  ██║     ██╔══╝  ██║        ██║       ██║   ██║██╔══██╗██║   ██║██╔══██║██║╚██╗██║██║ ███╔╝  ██╔══██║   ██║   ██║██║   ██║██║╚██╗██║
    // ███████║███████╗███████╗███████╗╚██████╗   ██║       ╚██████╔╝██║  ██║╚██████╔╝██║  ██║██║ ╚████║██║███████╗██║  ██║   ██║   ██║╚██████╔╝██║ ╚████║
    // ╚══════╝╚══════╝╚══════╝╚══════╝ ╚═════╝   ╚═╝        ╚═════╝ ╚═╝  ╚═╝ ╚═════╝ ╚═╝  ╚═╝╚═╝  ╚═══╝╚═╝╚══════╝╚═╝  ╚═╝   ╚═╝   ╚═╝ ╚═════╝ ╚═╝  ╚═══╝

    const {
        executeMutation: swapOrgAuthentication,
        mutationReturn: [_s, { loading: swapOrgAuthenticationLoading }],
    } = useSwapOrgAuthenticationMutation();

    const handleSelectOrganization = (organizationId: string) => {
        // When a user is invited, all the jwt details get handled in validateInvitedUserWithToken.
        if (invite) {
            toPage(path);
        } else {
            swapOrgAuthentication({
                variables: {
                    organizationId,
                },
                onCompleted: (result) => {
                    dispatch(
                        userActions.onAuthenticationValidation({
                            type: "success",
                            v: new Success(result.swapOrgAuthentication),
                        })
                    );

                    if (userIsAuthenticated) {
                        toPage(path);
                    } else {
                        toStepFallback();
                    }
                },
                onError: () => {
                    errorToast("Failed to authenticate with organization");
                    toStepFallback();
                },
            });
        }
    };

    // ██████╗ ██████╗  ██████╗ ██████╗ ██╗   ██╗ ██████╗████████╗    ██╗      ██████╗  ██████╗ ██╗███╗   ██╗
    // ██╔══██╗██╔══██╗██╔═══██╗██╔══██╗██║   ██║██╔════╝╚══██╔══╝    ██║     ██╔═══██╗██╔════╝ ██║████╗  ██║
    // ██████╔╝██████╔╝██║   ██║██║  ██║██║   ██║██║        ██║       ██║     ██║   ██║██║  ███╗██║██╔██╗ ██║
    // ██╔═══╝ ██╔══██╗██║   ██║██║  ██║██║   ██║██║        ██║       ██║     ██║   ██║██║   ██║██║██║╚██╗██║
    // ██║     ██║  ██║╚██████╔╝██████╔╝╚██████╔╝╚██████╗   ██║       ███████╗╚██████╔╝╚██████╔╝██║██║ ╚████║
    // ╚═╝     ╚═╝  ╚═╝ ╚═════╝ ╚═════╝  ╚═════╝  ╚═════╝   ╚═╝       ╚══════╝ ╚═════╝  ╚═════╝ ╚═╝╚═╝  ╚═══╝

    const { executeMutation: swapProductAuthentication } =
        useSwapProductAuthenticationMutation();

    const handleProductLogin = (input: ProductExtendedProductUserExtended) => {
        swapProductAuthentication({
            variables: { productId: input.product.id, isSupport: false },
            onCompleted: (result) => {
                dispatch(
                    userActions.onAuthenticationValidation({
                        type: "success",
                        v: new Success(result.swapProductAuthentication),
                    })
                );
                toPage(path);
            },
        });
    };

    return match(currentStep)
        .with({ step: SignInStep.email }, ({ email: initialEmail }) => (
            <LoginForm
                initialState={{ email: initialEmail ?? "" }}
                loading={loginLoading}
                signUpUrl={Routes.signUp}
                onLogin={({ email }) => handleSignIn(email)}
                avatarSrc={datascapeLogoPng}
                avatarAlt="BigGeo Logo"
                loginTitle="Datascape Log In"
                subTitle="Hi, Welcome back. Log into your Datascape space"
            />
        ))
        .with({ step: SignInStep.token }, ({ email }) => (
            <LoginSignUpCodeForm
                email={email}
                onValidate={(token) => handleValidate(email, token)}
                onResend={handleResend}
                onBack={() => toEmailStep(email)}
                loading={loginCodeLoading || inviteLoginCodeLoading}
                variant="signIn"
            />
        ))
        .with({ step: SignInStep.createOrgPrompt }, ({ user }) => (
            <CreateOrganizationPrompt
                firstName={unwrapMaybe(user.user.firstName)}
                lastName={unwrapMaybe(user.user.lastName)}
                img={unwrapMaybe(user.user.img)}
                onClickContinue={() => toCreateOrgStep(user)}
            />
        ))
        .with({ step: SignInStep.createOrg }, () => (
            <SetUpOrganizationInformationForm
                loading={
                    createOrganizationLoading || createOrganizationUserLoading
                }
                onContinue={handleOrganizationProfile}
                app={Applications.datascape}
                disableStepIndicator
                submitType="button"
            />
        ))
        .with({ step: SignInStep.selectOrg }, ({ user, organizations }) => (
            <LoginOrganizationSelectForm
                loading={swapOrgAuthenticationLoading}
                organizations={organizations.map((o) => ({
                    id: o.id,
                    name: o.name,
                    img: unwrapMaybe(o.img),
                }))}
                user={{
                    firstName: unwrapMaybe(user.user.firstName),
                    lastName: unwrapMaybe(user.user.lastName),
                    img: unwrapMaybe(user.user.img),
                }}
                subTitle={
                    organizations.length > 1
                        ? "Looks like you’re a member of multiple organizations, which Datascape space do you want to log into?"
                        : undefined
                }
                onSelect={handleSelectOrganization}
            />
        ))
        .with({ step: SignInStep.createProduct }, () => (
            <CreateEditDatascapeContainer onContinue={handleProductLogin} />
        ))
        .exhaustive();
};

export default SignInContainer;
